import { t as $t } from '@enercad/core';
import { api, type AiProviderView, type AiSettings } from './api';
import { esc, qs } from './ui';

/*
 * Provider, address, model and key fields of a company's AI settings. The model list comes from the
 * provider itself (with the key just typed, or the stored one), so new models show up without an update;
 * a few known names stand in while there is no key, and any other name can be typed.
 */

const provName = (p: AiProviderView) => (p.id === 'custom' ? $t('OpenAI uyumlu sunucu (Ollama, LM Studio, vLLM…)') : p.name);

type View = { settings: AiSettings; keySet: boolean; keyHint: string; providers: AiProviderView[] };

export function aiFieldsHtml(v: View): string {
  const s = v.settings;
  return `<div class="row2" style="align-items:start"><label class="fld"><span>${$t('Sağlayıcı')}</span><select id="aiprov">${v.providers.map((p) => `<option value="${p.id}"${s.provider === p.id ? ' selected' : ''}>${esc(provName(p))}</option>`).join('')}</select></label>
    <div class="fld"><span>${$t('Model')} <button type="button" class="btn" id="aimodelre" style="padding:0 6px" title="${$t('Sağlayıcıdan model listesini yeniden al')}">↻</button></span><select id="aimodelsel"></select>
      <input id="aimodel" autocomplete="off" spellcheck="false" data-1p-ignore data-lpignore="true" value="${esc(s.model)}"><div class="note" id="aimodelnote"></div></div></div>
    <label class="fld" id="aiurlrow"><span id="aiurllbl"></span><input id="aiurl" autocomplete="off" value="${esc(s.baseUrl)}"></label>
    <label class="fld"><span>${$t('API anahtarı')} ${v.keySet ? `<span class="pill ok">${$t('kayıtlı')} ${esc(v.keyHint)}</span>` : `<span class="pill warn">${$t('yok')}</span>`}</span>
      <input id="aikey" class="secret" autocomplete="off" spellcheck="false" autocapitalize="off" data-1p-ignore data-lpignore="true"></label>
    <p class="note" id="aikeyfrom" style="margin:-4px 0 9px"></p>`;
}

/** Fits the fields to the chosen provider and fetches its models. */
export function bindAiFields(root: HTMLElement, v: View) {
  const sel = qs<HTMLSelectElement>(root, '#aiprov'), ms = qs<HTMLSelectElement>(root, '#aimodelsel');
  const model = qs<HTMLInputElement>(root, '#aimodel'), url = qs<HTMLInputElement>(root, '#aiurl'), key = qs<HTMLInputElement>(root, '#aikey');
  const note = qs(root, '#aimodelnote');
  const prov = () => v.providers.find((x) => x.id === sel.value) ?? v.providers[0];
  let ask = 0;

  /** the list in the select; the current model stays chosen, or goes into the field when it is not listed */
  const fill = (list: { id: string; name?: string }[]) => {
    if (!model.value && list.length) model.value = list.find((m) => m.id === prov().models[0])?.id ?? list[0].id;
    const own = !list.some((m) => m.id === model.value);
    ms.innerHTML =
      list.map((m) => `<option value="${esc(m.id)}"${m.id === model.value ? ' selected' : ''}>${esc(m.name && m.name !== m.id ? `${m.name} · ${m.id}` : m.id)}</option>`).join('') +
      `<option value=""${own ? ' selected' : ''}>${$t('Başka bir model…')}</option>`;
    ms.hidden = !list.length;
    ms.disabled = false;
    model.hidden = !own;
  };

  const load = async () => {
    const p = prov(), typed = key.value.trim(), n = ++ask;
    const stored = p.id === v.settings.provider && v.keySet;
    if (!typed && !stored && !p.keyOptional) {
      fill(p.models.map((id) => ({ id })));
      note.textContent = $t('Anahtarı girince sağlayıcının güncel modelleri listelenir.');
      return;
    }
    if (!p.url && !url.value.trim()) {
      fill([]);
      note.textContent = '';
      return;
    }
    ms.hidden = false;
    ms.disabled = true;
    ms.innerHTML = `<option>${$t('Modeller yükleniyor…')}</option>`;
    note.textContent = '';
    try {
      const r = await api.tenantAiModels({ provider: p.id, baseUrl: url.value.trim(), ...(typed ? { apiKey: typed } : {}) });
      if (n !== ask) return;
      fill(r.models);
      note.textContent = r.models.length ? $t('Sağlayıcıdan {0} model', r.models.length) : $t('Sağlayıcı model listesi vermedi; model adını yazın.');
    } catch (e) {
      if (n !== ask) return;
      fill(p.models.map((id) => ({ id })));
      note.textContent = $t('Model listesi alınamadı: {0}', (e as Error).message);
    }
  };

  const fit = (changed: boolean) => {
    const p = prov();
    if (changed) {
      // a key typed for one provider never goes to another
      model.value = '';
      url.value = '';
      key.value = '';
    }
    model.placeholder = p.id === 'azure' ? $t('dağıtım (deployment) adı') : (p.models[0] ?? 'llama3.1');
    qs(root, '#aiurlrow').hidden = p.id === 'anthropic';
    qs(root, '#aiurllbl').textContent = p.url ? $t('Sunucu adresi (boş = {0})', p.url) : $t('Sunucu adresi (gerekli)');
    url.placeholder = p.url || (p.id === 'azure' ? 'https://<kaynak>.openai.azure.com/openai/v1' : 'https://ai.firmaniz.com/v1');
    const same = p.id === v.settings.provider && v.keySet;
    key.placeholder = same ? $t('Değiştirmek için yeni anahtarı yapıştırın') : p.keyOptional ? $t('Gerekmiyorsa boş bırakın') : p.id === 'anthropic' ? 'sk-ant-…' : '';
    qs(root, '#aikeyfrom').textContent = [
      p.keyFrom ? $t('Anahtarı {0} adresinden alabilirsiniz.', p.keyFrom) : '',
      p.pdf ? '' : $t("Bu sağlayıcı PDF okuyamaz; tek hat PDF'inin ilk sayfası resim olarak gönderilir."),
      changed && v.keySet && p.id !== v.settings.provider && !p.keyOptional ? $t('Sağlayıcı değişti; yeni sağlayıcının API anahtarını girin.') : '',
    ].filter(Boolean).join(' ');
    void load();
  };

  sel.onchange = () => fit(true);
  key.onchange = url.onchange = () => void load();
  qs(root, '#aimodelre').onclick = () => void load();
  ms.onchange = () => {
    model.hidden = !!ms.value;
    model.value = ms.value;
    if (!ms.value) model.focus();
  };
  fit(false);
}

/** The saved settings make a connection: a key, or an own server that needs none. */
export const aiConnected = (v: { settings: AiSettings; keySet: boolean; providers: AiProviderView[] }) =>
  v.keySet || (!!v.providers.find((p) => p.id === v.settings.provider)?.keyOptional && !!v.settings.baseUrl);

export function readAiFields(root: HTMLElement) {
  return {
    provider: qs<HTMLSelectElement>(root, '#aiprov').value,
    baseUrl: qs<HTMLInputElement>(root, '#aiurl').value.trim(),
    model: qs<HTMLInputElement>(root, '#aimodel').value.trim(),
    key: qs<HTMLInputElement>(root, '#aikey').value.trim(),
  };
}
